import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/articles";
import { topics } from "@/mocks/topics";

const PAGE_SIZE = 4;

export default function LatestNews() {
  const sorted = useMemo(
    () => [...articles].sort((a, b) => b.date.localeCompare(a.date)),
    []
  );
  const hot = useMemo(() => [...articles].sort((a, b) => b.views - a.views).slice(0, 6), []);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const sentinel = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinel.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible((prev) => (prev < sorted.length ? prev + PAGE_SIZE : prev));
        }
      },
      { rootMargin: "120px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [sorted.length]);

  return (
    <section className="bg-background-100/60 border-y border-background-200">
      <div className="mx-auto max-w-content px-4 md:px-8 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="flex items-end justify-between gap-4 mb-8">
              <div>
                <span className="text-xs font-medium tracking-[0.2em] text-accent-600">LATEST</span>
                <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  最新资讯
                </h2>
              </div>
              <span className="text-sm text-foreground-500 hidden sm:block">
                共 {sorted.length} 篇 · 按发布时间排序
              </span>
            </div>

            <div className="space-y-4">
              {sorted.slice(0, visible).map((article) => (
                <ArticleCard key={article.id} article={article} variant="horizontal" />
              ))}
            </div>

            <div ref={sentinel} className="h-1" />

            {visible < sorted.length ? (
              <button
                type="button"
                onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                className="mt-8 w-full py-3 rounded-md border border-background-300 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                加载更多资讯
              </button>
            ) : (
              <p className="mt-8 text-center text-sm text-foreground-400">已展示全部资讯</p>
            )}
          </div>

          <aside className="lg:col-span-4 space-y-8">
            <div className="bg-background-50 rounded-lg border border-background-200 p-5 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-fire-line text-accent-600 text-lg" />
                <h3 className="font-heading text-lg font-semibold text-foreground-900">热门推荐</h3>
              </div>
              <div className="flex flex-col">
                {hot.map((article, index) => (
                  <Link
                    key={article.id}
                    to={`/detail/${article.id}`}
                    className="group flex items-start gap-3 py-3 border-b border-background-200 last:border-0 cursor-pointer"
                  >
                    <span
                      className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold shrink-0 ${
                        index < 3 ? "bg-accent-500 text-secondary-950" : "bg-background-200 text-foreground-500"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <div className="min-w-0">
                      <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors leading-snug line-clamp-2">
                        {article.title}
                      </h4>
                      <span className="text-xs text-foreground-400 mt-1 block">
                        {article.categoryName} · {article.views.toLocaleString()} 阅读
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-primary-600 rounded-lg p-5 md:p-6 text-background-50">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-bookmark-3-line text-accent-400 text-lg" />
                <h3 className="font-heading text-lg font-semibold">文化专题</h3>
              </div>
              <div className="space-y-2">
                {topics.map((topic) => (
                  <Link
                    key={topic.id}
                    to={`/topic/${topic.id}`}
                    className="flex items-center justify-between gap-3 px-3 py-3 rounded-md bg-primary-700/50 hover:bg-primary-700 transition-colors cursor-pointer"
                  >
                    <div className="min-w-0">
                      <span className="block text-sm font-medium">{topic.title}</span>
                      <span className="block text-xs text-primary-200 mt-0.5 truncate">{topic.subtitle}</span>
                    </div>
                    <i className="ri-arrow-right-line text-accent-400 shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}